// Sugalarm — /api/alerts wrappers.
//
// Note: there is intentionally no /api/alerts/stats endpoint on the
// backend; the AlertsScreen renders its own counts from the listed rows.

import { request } from "../lib/apiClient";
import type {
  Alert,
  ApiResult,
  CustodyCheckinAnswer,
  CustodyCheckinResult,
  CustodyPromptKind,
} from "../lib/types";

export interface AlertListResponse {
  count: number;
  alerts: Alert[];
}

export function list(
  hours = 24,
  limit = 200,
  unacknowledgedOnly = false,
  // Cycle 433: abortable, so the wake reseed that lists these rows can be
  // bounded like the rest of it.
  signal?: AbortSignal,
): Promise<ApiResult<AlertListResponse>> {
  const params = new URLSearchParams({
    hours: String(hours),
    limit: String(limit),
  });
  if (unacknowledgedOnly) params.set("unacknowledged_only", "true");
  return request<AlertListResponse>(`/api/alerts?${params.toString()}`, { signal });
}

export function acknowledge(alertId: string): Promise<ApiResult<Alert>> {
  return request<Alert>(`/api/alerts/${alertId}/acknowledge`, { method: "POST" });
}

// ─── Cycle 38: cross-device alarm state sync ───────────────────────

/** "Address the Situation" — snoozes the alarm across every device on
 *  the account. Server clamps `durationSeconds` to [60, 1800]; omit to
 *  use the cycle-20.x 5-minute default. */
export function snooze(
  alertId: string,
  durationSeconds?: number,
): Promise<ApiResult<Alert>> {
  const body =
    durationSeconds != null ? { duration_seconds: durationSeconds } : {};
  return request<Alert>(`/api/alerts/${alertId}/snooze`, {
    method: "POST",
    body,
  });
}

/** "Silence" — drops audio across every device for the rest of the
 *  episode. The visual flash continues until the local recovery rule
 *  ends the episode on each device independently. */
export function silence(alertId: string): Promise<ApiResult<Alert>> {
  return request<Alert>(`/api/alerts/${alertId}/silence`, { method: "POST" });
}

/** Cycle 410 — "Sound the alarm now". ENDS a lead window so every device
 *  sounds; it can never open, extend or shorten one to anything but zero
 *  (the only reachable outcome is louder). No body. Idempotent — a row
 *  with no window is a 200 no-op; a closed episode is 409 `episode_closed`.
 *  Not throttled into any quiet bucket. */
export function soundNow(alertId: string): Promise<ApiResult<Alert>> {
  return request<Alert>(`/api/alerts/${alertId}/sound_now`, { method: "POST" });
}

/** Cycle 391 — "I'm treating this". Opens a bounded treating window on an
 *  urgent low.
 *
 *  Cycle 404: the duration is SERVER-ASSIGNED (a single ten minutes), so
 *  this call carries no duration at all. Everything about the window is
 *  server-derived.
 *
 *  A 409 carries a machine-readable code in `detail.code`; the one the UI
 *  must distinguish is `no_fresh_reading` (a dark sensor), because "the
 *  alarm is still sounding and here is why" is a completely different
 *  message from a generic failure. */
export function treat(
  alertId: string,
  contactId?: string | null,
  opts?: {
    /** Cycle 429: the stale row this tap was FIRST sent to and refused on.
     *  Goes onto the server's door log line as `retry_of`; read for
     *  nothing else. Sent at most once per tap. */
    retryOf?: string | null;
  },
): Promise<ApiResult<Alert>> {
  const body: Record<string, unknown> = {};
  // Cycle 392: `contact_id` is optional and omitted entirely when the
  // client does not know who tapped — identity is never a gate, so the
  // window opens on the first tap either way.
  if (contactId !== undefined) body.contact_id = contactId;
  if (opts?.retryOf) body.retry_of = opts.retryOf;
  return request<Alert>(`/api/alerts/${alertId}/treating`, {
    method: "POST",
    // ⚠ CYCLE 404: NO `duration_seconds`. There is one window, ten
    // minutes, and the SERVER assigns it — a client that sent a duration
    // would have it ignored anyway, so we stop pretending to choose.
    body,
  });
}

/** Cycle 392 — the second tap. Attaches a name to an ALREADY-RUNNING
 *  window; it can never open, extend, shorten or close one.
 *
 *  `null` is the explicit "Someone else" answer, which is a real outcome
 *  and not a failure to answer. There is no free-text path: the only
 *  accepted input is an id from the household's own curated list. */
export function setTreatingIdentity(
  alertId: string,
  contactId: string | null,
): Promise<ApiResult<Alert>> {
  return request<Alert>(`/api/alerts/${alertId}/treating/identity`, {
    method: "POST",
    body: { contact_id: contactId },
  });
}

/** Cycle 404 — "I can help after all". Reinstates the CALLER into this
 *  episode's call search after they pressed 3 on an escalation call.
 *
 *  The server derives WHICH contact from the caller's account
 *  (`emergency_contacts.linked_user_id`); there is deliberately no id in
 *  the request, so one account can never un-opt-out another hero.
 *
 *  Idempotent by design: a caller with no linked contact, or one who never
 *  opted out, gets a 200 and the row unchanged. A foreign / nonexistent
 *  alert is a 404 byte-identical to "missing" — the refusal must not
 *  confirm that somebody else's alarm exists.
 *
 *  Returns the refreshed AlertOut, so `episode_opt_outs` on the response is
 *  the authority for whether the action is still warranted. */
/** Cycle 407 → 427: answer the prompt on an open treating window, or
 *  press one of the treating screen's two standing actions. The body is
 *  `{answer, kind}` and NOTHING else — the glucose side is decided
 *  server-side from the live readings; a client cannot assert a number,
 *  a deadline or a count. `kind` names WHICH prompt a yes / no answers
 *  (the one on screen), so a stale answer to a superseded prompt is
 *  refused rather than landing on a question this device never saw.
 *  `help` / `resume` answer no question and only ever make the alarm
 *  louder. */
export function checkin(
  alertId: string,
  answer: CustodyCheckinAnswer,
  kind?: CustodyPromptKind | null,
): Promise<ApiResult<CustodyCheckinResult>> {
  return request<CustodyCheckinResult>(`/api/alerts/${alertId}/checkin`, {
    method: "POST",
    body: kind ? { answer, kind } : { answer },
  });
}

export function undoOptOut(alertId: string): Promise<ApiResult<Alert>> {
  return request<Alert>(`/api/alerts/${alertId}/optout/undo`, {
    method: "POST",
  });
}

export function acknowledgeAll(): Promise<ApiResult<{ acknowledged: number }>> {
  return request<{ acknowledged: number }>("/api/alerts/acknowledge_all", {
    method: "POST",
  });
}

export function unacknowledgedCount(): Promise<ApiResult<{ count: number }>> {
  return request<{ count: number }>("/api/alerts/unacknowledged_count");
}

// Cycle 310 — the consumer-scoped un-wedge truth check: fired,
// not-yet-server-recovered, non-synthetic alerts across every member the
// calling account can access. Consumed by the latched-episode
// reconciliation in useAlarmEpisode (and, natively, by the kiosk OTA
// updater's deferral cross-check).
export function activeCount(): Promise<ApiResult<{ active_alerts: number }>> {
  return request<{ active_alerts: number }>("/api/alerts/active_count");
}
